<script lang="ts" setup>
import mitt from 'mitt'

const props = defineProps<{
  commentAreaId?: number
  commentId: number
  floor?: number
  html: string
  user: User.BasicInfo
  replyCount?: number
  secondReply?: CommentSecondReply[]
  likeStatus: LikeStatus
  createdAt: number
  image?: string
  isOrigin?: boolean
  isDetail?: boolean
  isPin?: boolean
  isAuthor?: boolean
  isSelf?: boolean
  isDelete?: boolean
}>()

const screen = useScreen()
const emit = defineEmits(['onReply'])
const repliesCount = ref(props.replyCount || 0)

const $toast = useToast()
const $confirm = useConfirm()
const options = [
  {
    label: '删除此评论',
    key: 'delete',
    show: !props.isDelete && props.isSelf
  },
  {
    label: '置顶',
    key: 'pin',
    show: props.isSelf && !props.isPin
  },
  {
    label: '取消置顶',
    key: 'unpin',
    show: props.isSelf && props.isPin
  },
  {
    label: '举报此评论',
    key: 'report'
  }
]

const showReply = ref(false)
const richTextRef = ref<RichTextRefIface>()
const emitter = mitt<RichTextEvent>()
const replyContent = ref('')

const length = computed(() => richTextRef.value?.getContentLength() || 0)
const { data: detail } = useAsyncData(
  `commentAreaDetail:${props.commentAreaId}`,
  async () => {
    if (props.commentAreaId && props.isOrigin) {
      return fetchCommentAreaDetail(props.commentAreaId)
    } else {
      return null
    }
  }
)

const actions: Record<string, () => Promise<any>> = {
  pin: async () => {
    const res = await requestPinComment(props.commentId)
    if (res.code === 1) {
      $toast.success('置顶评论设置成功了喵~')
    } else {
      $toast.error(res.msg)
    }
  },
  unpin: async () => {
    const res = await requestPinComment(props.commentId, true)
    if (res.code === 1) {
      $toast.success('已经取消置顶了喵~')
    } else {
      $toast.error(res.msg)
    }
  },
  delete: async () => {
    $confirm('确认删除此评论？', '删除后无法恢复', {
      type: 'error',
      confirmButtonText: '删除',
      onConfirm: async () => {
        const res = await requestDeleteComment(props.commentId)
        if (res.code === 1) {
          $toast.success('成功删除该评论了喵~')
        } else {
          $toast.error(res.msg)
        }
      }
    })
  },
  report: async () => {
    useEmitter().emit('openReportForm', {
      resourceId: props.commentId,
      resourceType: 4
    })
  }
}

onMounted(() => {
  useEmitter().on('openCommentReplyInputInDetailPage', ({ commentId }) => {
    if (commentId === props.commentId) {
      showReply.value = true
      setTimeout(() => {
        richTextRef.value?.clear()
        richTextRef.value?.insertText('回复')
        richTextRef.value?.insertMention({
          value: props.user.name,
          id: props.user.id
        })
        richTextRef.value?.insertText('：')
      })
    } else {
      showReply.value = false
    }
  })
})

async function handleSelect(key: string) {
  const action = actions[key]
  if (action) {
    await action()
  }
}

function openReply() {
  useEmitter().emit('openCommentReplyInputInDetailPage', {
    commentId: props.commentId
  })
}

function openDetail() {
  useEmitter().emit('openCommentDetail', {
    commentId: props.commentId
  })
}

async function sendReply() {
  if (length.value === 0) {
    $toast.error('亲，至少输入点东西再发送吧')
    return
  }
  if (length.value > 200) {
    $toast.error('不能超过200字符哦喵~')
    return
  }
  const res = await requestCreateCommentReply(
    props.commentId,
    replyContent.value
  )
  if (res.code === 1) {
    showReply.value = false
    $toast.success('回复成功喵~')
    emit('onReply')
  } else {
    $toast.error(res.msg)
  }
}
</script>

<template>
  <div class="m-comment-card">
    <div class="m-comment-card__left">
      <UserAvatar
        :id="user.id"
        :src="user.avatar"
        size="md"
        :auth="user.user_auth"
      />
      <span v-if="isPin" class="m-comment-card__pin">置顶</span>
    </div>
    <div class="m-comment-card__right">
      <div class="m-comment-card__header">
        <MLink :to="`/member/${user.id}`" class="m-comment-card__author">
          <span
            :style="{
              color: user.name_color ? pipe.namecolor(user.name_color) : 'unset'
            }"
            class="m-comment-card__username"
          >
            {{ user.name }}
          </span>
          <span v-if="isAuthor" class="m-comment-card__landlord">作者</span>
        </MLink>
        <span class="m-comment-card__id">#{{ commentId }}</span>
        <div class="m-comment-card__operation">
          <n-dropdown
            trigger="click"
            :disabled="isDelete"
            :options="options"
            @select="handleSelect"
          >
            <n-icon
              :size="18"
              class="bi bi-three-dots-vertical icon"
              :class="{ disabled: isDelete }"
            />
          </n-dropdown>
        </div>
      </div>
      <div :class="{ isDelete }" v-html="xss(setTargetBlank(html))"></div>
      <MImagePreview v-if="image">
        <MImg :src="image" class="m-comment-card__image preview" />
      </MImagePreview>

      <m-link
        v-if="detail && isOrigin"
        :resource-id="detail.resource_id"
        :resource-type="detail.resource_type"
        title="查看原帖"
        link
        block
        class="m-comment-card__link"
      >
        查看原帖
      </m-link>
      <div class="m-comment-card__footer">
        <div v-if="floor" class="m-comment-card__floor">
          {{ floor }}楼&nbsp;&nbsp;
        </div>
        <div v-if="!screen.xs" class="m-comment-card__date">
          {{ pipe.date(createdAt) }}
        </div>
        <div class="m-comment-card__action">
          <div class="m-comment-card__action-item" @click="openReply">
            <m-icon icon="ni ni-comment">
              <span class="text">
                {{ pipe.num(repliesCount) || (!screen.xs ? '回复' : '') }}
              </span>
            </m-icon>
          </div>
          <div class="m-comment-card__action-item">
            <m-like-action
              :resource-id="commentId"
              :resource-type="4"
              :like-status="likeStatus"
              type="dislike"
            >
              <span class="text">
                {{
                  pipe.num(likeStatus.dislike.count) ||
                  (!screen.xs ? '不喜欢' : '')
                }}
              </span>
            </m-like-action>
          </div>

          <div class="m-comment-card__action-item">
            <m-like-action
              :resource-id="commentId"
              :resource-type="4"
              :like-status="likeStatus"
              type="like"
            >
              <span class="text">
                {{
                  pipe.num(likeStatus.like.count) || (!screen.xs ? '喜欢' : '')
                }}
              </span>
            </m-like-action>
          </div>
        </div>
      </div>
      <Transition>
        <div v-if="showReply">
          <RichText
            ref="richTextRef"
            v-model="replyContent"
            :emitter="emitter"
            :max-length="200"
            mention
            class="editor"
            toolbar="#create-reply-toolbar"
            placeholder="请输入友善的回复内容哦喵~"
            :formats="['sticker', 'mention']"
          />
          <RichTextToolBar
            id="create-reply-toolbar"
            :emitter="emitter"
            sticker
            mention
          >
            <n-button
              :disabled="length === 0"
              type="primary"
              size="small"
              @click="sendReply"
            >
              发送
            </n-button>
          </RichTextToolBar>
        </div>
      </Transition>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.v-enter-active,
.v-leave-active {
  transition: opacity 0.5s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}
.m-comment-card {
  padding: 24px 0;
  display: flex;
  .isDelete {
    color: var(--color-gray-300);
  }
  &__link {
    color: var(--color-blue-500);
    margin-top: 8px;
  }
  &__left {
    width: 40px;
    margin-right: 12px;
    display: flex;
    flex-direction: column;
  }
  &__pin {
    margin-top: 12px;
    width: 44px;
    height: 16px;
    line-height: 16px;
    background-color: var(--color-violet-50);
    color: var(--color-primary-600);
    font-size: 10px;
    font-weight: 500;
    text-align: center;
  }
  &__right {
    flex: 1;
    width: 0;
  }
  &__header {
    height: 44px;
    vertical-align: middle;
    display: flex;
    align-items: center;
  }
  &__author {
    height: 30px;
    display: flex;
    align-items: center;
    margin-right: auto;
  }
  &__username {
    display: inline-block;
    font-size: 14px;
    line-height: 16px;
  }
  &__id {
    display: inline-block;
    margin-left: 8px;
    padding: 2px 4px;
    height: 16px;
    line-height: 12px;
    color: var(--color-gray-600);
    font-weight: 500;
    text-align: center;
    font-size: 12px;
    border-radius: 4px;
    vertical-align: middle;
  }
  &__landlord {
    display: inline-block;
    margin-left: 8px;
    padding: 2px 4px;
    height: 16px;
    line-height: 12px;
    background-color: var(--color-violet-50);
    color: var(--color-primary-600);
    font-weight: 500;
    text-align: center;
    font-size: 12px;
    border-radius: 4px;
    vertical-align: middle;
  }
  &__image {
    width: 256px;
    max-height: 256px;
    border-radius: 6px;
  }
  &__floor,
  &__date {
    font-size: 12px;
    color: var(--color-gray-600);
  }
  &__operation {
    display: flex;
    align-items: center;
    .icon {
      cursor: pointer;
      padding: 0 8px;
      color: var(--color-gray-500);
    }
    .disabled {
      color: transparent;
      cursor: default;
    }
  }
  &__footer {
    height: 44px;
    vertical-align: middle;
    display: flex;
    align-items: center;
  }
  &__action {
    margin-left: auto;
    display: flex;
    justify-content: space-around;
    padding: 20px 0;
    color: var(--color-gray-400);
    &-item {
      display: flex;
      align-items: center;
      cursor: pointer;
      margin-left: 18px;
    }
    :deep(.n-icon) {
      font-size: 18px;
      margin-right: 8px;
    }
    .text {
      font-size: 12px;
      user-select: none;
    }
  }
  :deep(.rich-text-toolbar) {
    padding: 8px 0;
  }
  &__second {
    padding: 12px;
    border-radius: 4px;
    margin-top: 8px;
    background-color: var(--color-zinc-50);
    cursor: default;
    > div {
      margin-bottom: 12px;
    }
    :deep(.m-user__name) {
      color: var(--color-gray-600);
    }
    .content {
      margin-left: 30px;
      font-size: 14px;
      color: var(--color-gray-700);
    }
    &-detail {
      color: var(--color-gray-700);
      height: 27px;
      line-height: 27px;
      text-align: center;
      cursor: pointer;
      margin-top: 12px;
      margin-bottom: 0 !important;
    }
  }
}
</style>
